// The overtime band, mirroring accrue_overtime()'s `ot_seconds`
// (supabase/migrations/20260908110000_leave_ledger_writers.sql:125-149).
//
// This exists so Timesheets can show overtime WHILE it is being worked. The
// ledger cannot: accrue_overtime settles a day only once it closes
// (20260911100000), which is correct and is also why the tile had nothing to
// show mid-shift.
//
// Keep it a mirror. The arithmetic looks like "hours minus the contracted day"
// and is not — two of its four branches do not subtract the standard day at all:
//
//   ordinary workday              max(0, worked − standard)
//   public holiday                the WHOLE day, countWeekends ignored
//   weekend, countWeekends off    0
//   weekend, countWeekends on     the WHOLE day, not worked − standard
//
// A public holiday that falls on a configured weekend takes the holiday branch,
// because the SQL guard is `not is_workday AND not is_holiday` — so it banks in
// full even with countWeekends off. That is the branch a careful reading still
// gets wrong, and it has its own test.
//
// ── What this deliberately does NOT mirror ───────────────────────────────────
// `leaves_credited`. maxPerMonth and maxPerYear clamp it against the rest of the
// month's ledger, which the client does not have — so "you have earned a
// comp-off" computed here would be a guess, and a guess about somebody's leave
// balance is the kind that gets believed.
import { isWorkday, type Workdays } from './workdays';

/**
 * Overtime seconds banked for one day.
 *
 * `workedSeconds` must be the same quantity the server sums: the day's finalized
 * hours plus approved compensated hours. The live caller passes elapsed clock
 * time instead, which is the same quantity plus the open segment — that is the
 * whole point, and it is why the tile labels the figure provisional rather than
 * printing it as settled.
 */
export const overtimeSeconds = (a: {
  /** Org-local day key, 'YYYY-MM-DD'. */
  iso: string;
  workedSeconds: number;
  /** person_standard_seconds(): the person's contracted day, org day as fallback. */
  standardSeconds: number;
  workdays: Workdays | null;
  holidays: ReadonlySet<string>;
  countWeekends: boolean;
}): number => {
  // isWorkday already ANDs the weekday map with the holiday exclusion, which is
  // exactly is_org_workday(). Delegated rather than re-derived — the last time
  // this kind of arithmetic was inlined per screen it produced three answers.
  const workday = isWorkday(a.iso, a.workdays, a.holidays);
  const holiday = a.holidays.has(a.iso);

  if (!workday && !holiday && !a.countWeekends) return 0;
  return Math.max(0, a.workedSeconds - (workday ? a.standardSeconds : 0));
};
